import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { type MessageKey, t } from '../i18n';
import type { Me } from '../lib/auth';
import { useMe } from '../lib/auth';
import { type IconName, ModuleIcon } from './icons';
import { NotificationsBell } from './NotificationsBell';
import { Alert } from './ui';
import { UserMenu } from './UserMenu';

/** Usuario final: sin ningún permiso en ninguna organización. Ve el portal simple en lugar de los módulos. */
export const isPortalUser = (me: Me) => Object.values(me.permissions).every((keys) => keys.length === 0);

// Lo que usa un colaborador: sus tickets, sus tareas, las solicitudes de proyecto (las suyas y las que aprueba) y las
// guías de autoservicio.
const LINKS: Array<{ to: string; label: MessageKey; icon: IconName; end?: boolean }> = [
  { to: '/', label: 'menu.inicio', icon: 'inicio', end: true },
  { to: '/tickets', label: 'portal.mis_tickets', icon: 'tickets' },
  { to: '/tareas', label: 'portal.mis_tareas', icon: 'tareas' },
  { to: '/proyectos/solicitudes', label: 'portal.solicitudes', icon: 'proyectos' },
  { to: '/conocimiento', label: 'portal.guias', icon: 'conocimiento' },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex shrink-0 items-center gap-2 border-b-2 px-1 py-3 text-sm transition-colors ${isActive ? 'border-senal font-medium text-white' : 'border-transparent text-white/70 hover:text-white'}`;

/**
 * Portal del usuario final: barra superior con lo que usa y el contenido debajo. El inicio dibuja su propia franja
 * de bienvenida a lo ancho; el resto de las páginas va en una columna centrada.
 */
export function PortalLayout() {
  const me = useMe();
  const { pathname } = useLocation();
  const pendingRoles = [...new Set(me.rolesPendingMfa.map((r) => r.roleName))];
  return (
    <div className="flex min-h-full flex-col">
      <header className="bg-tinta text-white">
        {/* Una sola navegación: en pantallas chicas baja a una segunda fila con desplazamiento horizontal. */}
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-8 px-4 md:flex-nowrap md:px-8">
          <Link to="/" className="shrink-0 rounded-sm py-3 text-lg font-semibold tracking-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-senal">
            {t('app.nombre')}
          </Link>
          <nav aria-label="Módulos" className="order-last -mx-1 flex w-full gap-6 overflow-x-auto px-1 md:order-none md:mx-0 md:w-auto md:flex-1 md:px-0">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
                <ModuleIcon name={l.icon} className="h-[18px] w-[18px]" />
                {t(l.label)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <NotificationsBell dark />
            <UserMenu dark />
          </div>
        </div>
      </header>

      <main className="flex-1">
        {pendingRoles.length > 0 && (
          <div className="mx-auto max-w-6xl px-4 pt-4 md:px-8">
            <Alert tone="warning">
              {t('aviso.activar_2fa', { roles: pendingRoles.join(', ') })}{' '}
              <Link to="/perfil/seguridad" className="font-medium underline">
                {t('aviso.activar_2fa_accion')}
              </Link>
            </Alert>
          </div>
        )}
        {pathname === '/' ? (
          <Outlet />
        ) : (
          <div className="mx-auto max-w-6xl space-y-5 p-4 md:p-8">
            <Outlet />
          </div>
        )}
      </main>
    </div>
  );
}
